import { useState, useCallback } from 'react'
import { OcrResult } from '../../../services/ocr/ocr-types'

interface OcrResultsOverlayProps {
  results: OcrResult[]
  pageWidth: number
  pageHeight: number
  onClose: () => void
}

export function OcrResultsOverlay({ results, pageWidth, pageHeight, onClose }: OcrResultsOverlayProps) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)

  const handleCopy = useCallback((e: React.MouseEvent, result: OcrResult, idx: number) => {
    e.stopPropagation()
    navigator.clipboard.writeText(result.text).catch(() => {})
    setCopiedIdx(idx)
    setTimeout(() => setCopiedIdx(null), 1200)
  }, [])

  if (results.length === 0) return null

  return (
    <div className="absolute inset-0 z-20 pointer-events-none">
      <div className="relative w-full h-full">
        {results.map((result, i) => {
          const isCopied = copiedIdx === i
          return (
            <button
              key={i}
              onClick={(e) => handleCopy(e, result, i)}
              className="absolute pointer-events-auto group border-2 border-dashed border-accent/60 hover:border-accent rounded-sm transition-colors cursor-pointer bg-accent/[0.06] hover:bg-accent/15"
              style={{
                left: `${(result.box.x / pageWidth) * 100}%`,
                top: `${(result.box.y / pageHeight) * 100}%`,
                width: `${(result.box.width / pageWidth) * 100}%`,
                height: `${(result.box.height / pageHeight) * 100}%`,
              }}
              title="Click to copy text"
            >
              <div
                className={`absolute left-1/2 -translate-x-1/2 -bottom-7 whitespace-nowrap rounded-md px-2 py-0.5 text-xs shadow-lg transition-all ${
                  isCopied
                    ? 'bg-accent text-white opacity-100'
                    : 'bg-bg-surface border border-border-default text-fg-primary opacity-0 group-hover:opacity-100'
                }`}
              >
                {isCopied ? 'Copied!' : result.text}
                {!isCopied && (
                  <span className="text-fg-muted ml-1">
                    ({Math.round(result.confidence * 100)}%)
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>

      <button
        onClick={onClose}
        className="absolute top-2 right-2 pointer-events-auto bg-bg-surface border border-border-default rounded-lg w-8 h-8 flex items-center justify-center text-fg-secondary hover:text-fg-primary shadow-md"
      >
        ✕
      </button>
    </div>
  )
}
